import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { FilterCondition } from "go-list/core/components/Filter/services/types";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { GoListSegmentType, ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import AppFormatResourceStatusLabel from "../../../../../../../../../../components/FormatStatus";
import { StockApi } from "../../../../../../../../../../services/Api/Organization/types";

interface ListState {
	resource?: Record<string, any>;
	path: string;
	mobileActions: MobileActionProps[];
}

const listName = "STOCK";
const resourceType = "STOCK";

const buildConfig = (
	t: TFunction,
	onDelete: (id: number) => Promise<void>,
	data: Record<string, any>,
	location: Location,
	segmentListService: SegmentListService,
	organization: OrganizationState
) => {
	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				type: "text",
			},
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					ENABLED: t("enums.common.status.ENABLED", { ns: "lib" }),
					DELETED: t("enums.common.status.DELETED", { ns: "lib" }),
				},
				render: (item: StockApi) => <AppFormatResourceStatusLabel status={item.status} />,
				renderExport: (item: StockApi) => t(`enums.common.status.${item.status}`, { ns: "lib" }),
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: StockApi) => `${location.pathname}/${item.id}/edit`,
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: StockApi) => onDelete(item.id),
				visible: (item: StockApi) => item.status === "ENABLED",
				refreshListAfter: true,
			},
		],
		selectedColumns: ["name", "statuses"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
			{
				id: "deleted",
				name: t("go_list.filters.deleted", { ns: "lib" }),
				slug: "deleted",
				filters: [
					{
						filterId: "statuses",
						value: "DELETED",
						condition: "e" as FilterCondition,
					},
				],
			} as GoListSegmentType,
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.stocks.header.title"),
			filename: t("modules.stocks.header.title"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) =>
			apiOrganization.getStocks(params, { cancelToken: sourceToken?.token }),
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource, path, mobileActions }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const confirmation = useConfirmation();
	const { addSuccessFlash } = useFlash();
	const segmentContextValue = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	const onDelete = async (id: number) => {
		await confirmation({
			title: t("common.word.confirmation", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await apiOrganization.removeStock(id);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	if (!resource) return null;

	const data = resource.read();
	const config = buildConfig(t, onDelete, data, location, segmentContextValue, organization);

	return (
		<ListData
			config={config}
			emptyList={{ addAction: () => history.push(`${path}/new`) }}
			mobileActions={mobileActions}
		/>
	);
};

const OrganizationSettingsStockIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.stocks.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			variant: "primary",
			path: `${props.match.url}/new`,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
		},
	];

	return (
		<>
			{!isMobile && <Header title={t("modules.stocks.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} path={props.match.url} mobileActions={mobileActions} {...props} />
			</Suspense>
		</>
	);
};

export default OrganizationSettingsStockIndexPage;
